<template>
  <div
    ref="reference"
    class="tt-select-box"
    :class="[{ opened: isOpen, disabled: disabled }]"
    @click.prevent="toggleMenu"
  >
    <div
      v-if="$slots.input"
      tabindex="0"
      class="tt-select-box-custom-input"
      @blur="isOpen = false"
    >
      <slot :data="selected" name="input" />
    </div>
    <template v-else>
      <input
        v-bind="$attrs"
        :disabled="disabled"
        class="tt-select-box-input"
        :value="showText"
        :placeholder="placeholder"
        readonly
        @blur="isOpen = false"
      />
      <span class="tt-select-box-icon" />
    </template>
    <transition name="transition-drop">
      <Dropdown
        v-show="isOpen"
        ref="drop"
        v-transfer-dom
        :data-transfer="true"
        :style="dStyle"
        transfer
      >
        <ul
          ref="menu"
          v-click-outside="clickOutSide"
          class="tt-select-box-menu"
        >
          <li
            v-for="(option, idx) in optionList"
            :key="idx"
            :class="{ selected: option.val === selected.val }"
            :value="option.val"
            @mousedown="select(option)"
          >
            {{ option.text || $t(option.i18nKey) }}
          </li>
        </ul>
      </Dropdown>
    </transition>
  </div>
</template>
<script>
import Dropdown from 'com/Dropdown'
export default {
  name: 'SelectBox',
  components: {
    Dropdown
  },
  props: {
    value: [String, Number, Object],
    disabled: Boolean,
    placeholder: String,
    options: Array,
    labelInValue: Boolean,
    tabindex: { type: Number, default: 0 },
    dropdownMatchSelectWidth: {
      type: Boolean | Number,
      default: true
    },
    firstAsDefault: {
      type: Boolean,
      default: true
    },
    dropdownStyle: Object
  },
  data: () => ({
    isOpen: false,
    selected: { text: '', val: null },
    containerWidth: null
  }),
  computed: {
    showText() {
      if (this.selected) {
        return this.selected.text || this.$t(this.selected.i18nKey)
      }
    },
    optionList() {
      if (!this.options || !this.options.length) {
        return []
      }

      // Convert Array[string] to Array[object]
      if (typeof this.options[0] === 'string') {
        return this.options.map(item => ({
          text: item,
          val: item
        }))
      }

      return this.options
    },
    isOrderForm() {
      return this.$store.getters.hasTradeRequest
    },
    dStyle() {
      const style = { ...this.dropdownStyle }

      if (this.containerWidth && this.dropdownMatchSelectWidth) {
        style.width = this.containerWidth + 'px'
      }
      return style
    }
  },
  watch: {
    value() {
      this.update()
    },
    options() {
      this.update()
    },
    isOpen(val) {
      if (val) {
        if (this.dropdownMatchSelectWidth) {
          this.getContainerWidth()
        }
        this.$refs.drop.update()
      } else {
        this.$refs.drop.destroy()
      }
    }
  },
  mounted() {
    this.update()
  },
  methods: {
    toggleMenu() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
    },
    update() {
      const selected = this.optionList.find(
        item => item.val === this.getCurrentValue(this.value)
      )
      if (selected) {
        this.selected = selected
      } else {
        if (this.firstAsDefault && this.optionList[0]) {
          this.selected = this.optionList[0]
        } else {
          this.selected = { val: '', text: '' }
        }
      }
    },
    getCurrentValue(item) {
      if (item && typeof item === 'object') {
        return item.val
      }
      return item
    },
    getContainerWidth() {
      this.$nextTick(() => {
        this.containerWidth = this.$el.offsetWidth
      })
    },
    select(option) {
      this.selected = option
      if (this.labelInValue) {
        this.$emit('input', this.selected)
      } else {
        this.$emit('input', this.selected.val)
      }
      this.isOpen = false
    },
    clickOutSide(e) {
      const ref = this.$refs.reference
      const menu = this.$refs.menu
      const target = e.target
      const inside =
        ref && menu && (ref.contains(target) || menu.contains(target))
      if (!inside) {
        this.isOpen = false
      }
    }
  }
}
</script>
<style lang="less">
@import '~uikit';
@prefix-cls: tt-select-box;
@box-height: 32px;
@box-content-height: 30px;
.@{prefix-cls} {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: 120px;
  min-width: 90px;
  outline: none;
  cursor: pointer;
  &.opened {
    border-color: var(--active-color);
  }
  &.opened {
    .@{prefix-cls}-input {
      border-color: var(--active-color);
      color: var(--active-color);
    }
    .@{prefix-cls}-icon {
      background-color: var(--active-color);
    }
  }
  &.disabled {
    opacity: 0.8;
    cursor: default;
  }
}

.@{prefix-cls}-input {
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: @box-height;
  font-size: 12px;
  line-height: @box-content-height;
  padding-left: 10px;
  padding-right: 40px;
  border-radius: 2px;
  border: solid 1px var(--special-color);
  background: transparent;
  overflow: hidden;
  position: relative;
  color: @light-grey;
  outline: none;
}
.@{prefix-cls}-input:not(:disabled):hover {
  color: @light-grey;
  background: transparent;
}
.@{prefix-cls}-input:not(:disabled):focus {
  color: @light-grey;
  background: transparent;
}
.@{prefix-cls}-custom-input {
  display: block;
  cursor: pointer;
  outline: none;
}
.@{prefix-cls}-icon {
  width: @box-content-height;
  height: @box-content-height;
  position: absolute;
  right: 1px;
  top: 1px;
  background-color: var(--special-color);
  &:after {
    content: ' ';
    position: absolute;
    width: 0;
    height: 0;
    border: 4px solid transparent;
    border-right-color: #838d9e;
    border-bottom-color: #838d9e;
    bottom: 3px;
    right: 10px;
    top: 9px;
    transform: rotate(45deg);
  }
}

.@{prefix-cls}-menu {
  width: 100%;
  min-width: 90px;
  margin: 8px 0 0;
  padding: 0;
  border-radius: 2px;
  background-color: var(--drop-list);
  box-shadow: 2px 2px 10px var(--bg-default);
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 300px;
  z-index: 1000;
  li {
    margin: 0;
    padding: 8px;
    color: @light-grey;
    line-height: 1;
    font-size: 12px;
    list-style: none;
    cursor: pointer;
  }
  li:not(.selected):hover {
    color: var(--default-font-color);
    background-color: var(--hover-color);
  }
  .selected {
    color: var(--active-color);
  }
}
</style>
